import { ref, computed } from 'vue';
import { onboardingService } from '@/services/onboardingService';
import { useUserStore } from '@/store/userStore';

// Shared state so banner + modal don't each guess before the API responds.
const status = ref(null);
const loading = ref(false);
const error = ref(null);
let fetchPromise = null;

export function useOnboarding() {
  const userStore = useUserStore();

  const organization = computed(() => userStore.currentOrganization);
  const role = computed(() => userStore.currentOrganization?.role);

  const needsIntake = computed(() => {
    if (!status.value) return false;
    return status.value.needs_intake;
  });

  const needsChecklist = computed(() => {
    if (!status.value) return false;
    return status.value.needs_checklist;
  });

  const autoShowChecklist = computed(() => {
    if (!status.value) return false;
    return status.value.auto_show_checklist;
  });

  const progress = computed(() => status.value?.progress || {});

  const fetchStatus = async () => {
    if (fetchPromise) return fetchPromise;

    loading.value = !status.value;
    error.value = null;

    fetchPromise = onboardingService
      .getStatus()
      .then((data) => {
        status.value = data;
        return data;
      })
      .catch((err) => {
        error.value = err.message;
        throw err;
      })
      .finally(() => {
        loading.value = false;
        fetchPromise = null;
      });

    return fetchPromise;
  };

  const completeIntake = async (skipped = {}) => {
    const updated = await onboardingService.completeIntake(skipped);
    if (organization.value) {
      userStore.currentOrganization = {
        ...organization.value,
        settings: updated.settings,
      };
    }
    status.value = null;
    await fetchStatus();
    return updated;
  };

  const dismissChecklist = async () => {
    const updated = await onboardingService.dismissChecklist();
    if (organization.value) {
      userStore.currentOrganization = {
        ...organization.value,
        settings: updated.settings,
      };
    }
    status.value = null;
    await fetchStatus();
    return updated;
  };

  const resetOnboardingStatus = () => {
    status.value = null;
    error.value = null;
    fetchPromise = null;
  };

  return {
    status,
    loading,
    error,
    needsIntake,
    needsChecklist,
    autoShowChecklist,
    progress,
    fetchStatus,
    completeIntake,
    dismissChecklist,
    resetOnboardingStatus,
  };
}
